import LangEnum from '../../../../lang/LangEnum';
import { ResourceType } from '../../../../modules/users/ability.types';
import { DynamicOptionsSelectorsCreatorProps } from '../components/DynamicOptionsSelectorsCreator/dynamicOptionsSelectorsCreator.types';
import {
  CleanFilter,
  GetSearchParamKeys,
  HandleResetToDefaultValue,
} from '../hooks/commonHookReturn.types';
import useDateRangeFilter from '../hooks/dateRangeFilter/useDateRangeFilter';
import useMultiDynamicOptionsSelector from '../hooks/multiOptionsSelectorFilter/useMultiDynamicOptionsSelector/useMultiDynamicOptionsSelector';
import {
  GenericQueryOutput,
  GenericQueryParams,
} from '../hooks/multiOptionsSelectorFilter/useMultiDynamicOptionsSelector/useMultiDynamicOptionsSelector.types';
import useMultiStaticOptionsSelector from '../hooks/multiOptionsSelectorFilter/useMultiStaticOptionsSelector';
import useNumberRangeFilter from '../hooks/numberRangeFilter/useNumberRangeFilter';
import useRadioFilter from '../hooks/radioFilter/useRadioFilter';
import useTextFilter from '../hooks/textFilter/useTextFilter';

export type ReturnTypeOfUseMultiDynamicOptionsSelector = Omit<
  ReturnType<typeof useMultiDynamicOptionsSelector>,
  'handleResetFilter' | 'getSearchParamKeys' | 'cleanFilter' | 'handleResetToDefaultValue'
>;
type ReturnTypeOfUseMultiStaticOptionsSelector = Omit<
  ReturnType<typeof useMultiStaticOptionsSelector>,
  'cleanFilter' | 'getSearchParamKeys' | 'handleResetToDefaultValue'
>;
export type ReturnTypeOfUseDateRangeFilter = Omit<
  ReturnType<typeof useDateRangeFilter>,
  'cleanFilter' | 'getSearchParamKeys' | 'handleResetToDefaultValue'
>;
type ReturnTypeOfUseNumberRangeFilter = Omit<
  ReturnType<typeof useNumberRangeFilter>,
  'cleanFilter' | 'getSearchParamKeys' | 'handleResetToDefaultValue'
>;
export type ReturnTypeOfUseTextFilter = Omit<
  ReturnType<typeof useTextFilter>,
  'getSearchParamKeys' | 'handleResetToDefaultValue'
>;
type ReturnTypeOfUseRadioFilter = Omit<
  ReturnType<typeof useRadioFilter>,
  'cleanFilter' | 'getSearchParamKeys' | 'handleResetToDefaultValue'
>;

export type FilterWithReset<T extends boolean> = T extends true
  ? { handleResetToDefaultValue: HandleResetToDefaultValue }
  : {};
interface FilterFieldProps {
  cleanFilter?: CleanFilter | null;
  getSearchParamKeys: GetSearchParamKeys;

  toHide?: boolean;
  fixedFilter?: boolean;
  fixed?: boolean;
}

export enum PossibleFilters {
  OPTIONS_SELECTOR_WITH_DYNAMIC_DATA = 'dynamicOptionsSelector',
  OPTIONS_SELECTOR_CREATOR = 'dynamicOptionsSelectorsCreator',
  OPTIONS_SELECTOR_WITH_STATIC_DATA = 'staticOptionsSelector',
  DATE_RANGE = 'dateRange',
  NUMBER_RANGE = 'numberRange',
  NUMBER_RANGE_V2 = 'NUMBER_RANGE_V2',
  SEARCH_BAR = 'text',
  RADIO = 'radio',
}

export interface MultiDynamicOptionsFilter extends FilterFieldProps {
  type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA;
  multiDynamicOptionsArgs: ReturnTypeOfUseMultiDynamicOptionsSelector;
  title: string;
  label?: string;
  chipLabel?: string;
  preFetchOptions?: boolean;
  resourceType?: ResourceType;
  fixedFilter?: boolean;
}

// TODO: change this interface when FilterV2 is removed
export interface MultiDynamicOptionsCreatorFilter<
  FilterQueryParams extends GenericQueryParams = any,
  FilterQueryOutput extends GenericQueryOutput = any,
  DynamicOptionsQueryParams extends GenericQueryParams = any,
  DynamicOptionsQueryOutput extends GenericQueryOutput = any,
> extends FilterFieldProps {
  type: PossibleFilters.OPTIONS_SELECTOR_CREATOR;
  getLabel: (valuesToTranslate: Record<'title', string>) => string;
  title: string;
  groupBy: LangEnum;
  resourceType?: ResourceType;
  multiDynamicOptionsCreatorArgs: Pick<
    DynamicOptionsSelectorsCreatorProps<
      FilterQueryParams,
      FilterQueryOutput,
      DynamicOptionsQueryParams,
      DynamicOptionsQueryOutput
    >,
    | 'dynamicOptionQueryFn'
    | 'dynamicOptionQueryKey'
    | 'hookArgs'
    | 'filtersQueryFn'
    | 'filtersQueryKey'
    | 'filtersQueryParams'
    | 'keys'
    | 'isOfflineMode'
  >;
}
export interface MultiStaticOptionFilter extends FilterFieldProps {
  type: PossibleFilters.OPTIONS_SELECTOR_WITH_STATIC_DATA;
  multiStaticOptionsArgs: ReturnTypeOfUseMultiStaticOptionsSelector;
  title: string;
  label?: string;
  hideSearchField?: boolean;
  resourceType?: ResourceType;
}

export interface DateRangeFilter extends FilterFieldProps {
  type: PossibleFilters.DATE_RANGE;
  dateRangeArgs: ReturnTypeOfUseDateRangeFilter;
  title: string;
}

export interface PriceRangeFilter extends FilterFieldProps {
  type: PossibleFilters.NUMBER_RANGE;
  numberRangeArgs: ReturnTypeOfUseNumberRangeFilter;
  title: string;
  resourceType?: ResourceType;
}

export interface PriceRangeFilterV2 extends FilterFieldProps {
  type: PossibleFilters.NUMBER_RANGE_V2;
  numberRangeArgs: ReturnTypeOfUseNumberRangeFilter;
  title: string;
  resourceType?: ResourceType;
}

interface TextFilter extends FilterFieldProps {
  type: PossibleFilters.SEARCH_BAR;
  textArgs: ReturnTypeOfUseTextFilter;
  label: LangEnum;
  title: string;
}

export interface RadioButtonsFilter extends FilterFieldProps {
  type: PossibleFilters.RADIO;
  radioArgs: ReturnTypeOfUseRadioFilter;
  title: string;
  resourceType?: ResourceType;
  customChipLabel?: string;
}

export type FilterProps<IncludeResetToDefaultValue extends boolean = false> = (
  | MultiDynamicOptionsFilter
  | MultiDynamicOptionsCreatorFilter
  | MultiStaticOptionFilter
  | DateRangeFilter
  | PriceRangeFilter
  | PriceRangeFilterV2
  | TextFilter
  | RadioButtonsFilter
) &
  FilterWithReset<IncludeResetToDefaultValue>;

export type FilterOptionV3 = FilterProps & {
  groupBy: LangEnum;
  fixed?: boolean;
  fixedFilter?: boolean;
};
